import {customersApiClient} from './apiClient';

/**
 * The `ref`-keyed claim flow behind `/authorize`.
 *
 * A handoff link arrives as `…/rides/{id}/confirm?ref=AL-…&layout=minimal`. The `ref` is
 * an opaque, stable handle on the trip — NOT a credential and NOT a session: holding one
 * only lets you ask for an OTP to the phone already on file for that booking. The
 * passenger proves ownership with that code, and only then do we get a token.
 *
 * ⚠️ THIS PORTAL NEVER CALLS `/api/v1/support`. These endpoints are written up in the
 * support API guide because the phone desk is what creates the booking, but the section
 * they live in is "For the PASSENGER FE" and the caller is this app — so they belong on
 * the passenger leg like every other request here. An earlier cut built them as
 * `/api/v1/support/phone-booking/…`; that was wrong. The support leg is the staff desk's,
 * and `hopn-phone-booking` is the only portal that may call it.
 */
const PHONE_BOOKING_BASE = '/phone-booking';

// Everything here goes through customersApiClient → https://…/api/v1/passengers, so a ref
// call resolves to `/api/v1/passengers/phone-booking/{ref}`. The three claim calls need no
// token (they are how you GET one); `checkRefOwnership`'s trip probe uses the session.

/**
 * Masked pre-auth summary for the "we texted •••1234" line and the identity flag.
 * Deliberately thin: refs get forwarded, so the backend never returns the full phone or
 * the addresses. Unknown ref → 404 `quote_not_found`.
 *
 * @returns {{success: boolean, data: object|null, message: string, statusCode?: number}}
 */
export async function getPhoneBookingSummary(ref) {
    const result = await customersApiClient.get(`${PHONE_BOOKING_BASE}/${encodeURIComponent(ref)}`);
    return {
        success: !!result.success,
        // {trip_id, trip_reference, status, scheduled_at, final_price, phone_last4,
        //  passenger_claimed, requires_identity}
        data: result.success ? (result.data || null) : null,
        message: result.message || result.error || '',
        statusCode: result.statusCode,
    };
}

/**
 * Sends the OTP to the phone ON FILE — the passenger never types their number and this
 * app never sees it; `phone_last4` is all we get back to name it on screen.
 * No phone on the account → 422 `no_phone_on_file`.
 */
export async function requestPhoneBookingOtp(ref) {
    const result = await customersApiClient.postJson(
        `${PHONE_BOOKING_BASE}/${encodeURIComponent(ref)}/request-otp`,
        {},
    );
    return {
        success: !!result.success,
        data: result.success ? (result.data || null) : null,
        key: result.key,
        message: result.message || result.error || '',
        statusCode: result.statusCode,
    };
}

/**
 * Exchanges the code for a Sanctum token. Success also CLAIMS the account — the backend
 * stamps `phone_verified_at`, so this passenger's later notification links are signed
 * again and they never see this page for a second trip. Bad code → 401 `invalid_otp`.
 *
 * Never log `otp` or the returned token.
 */
export async function verifyPhoneBookingOtp(ref, otp) {
    const result = await customersApiClient.postJson(
        `${PHONE_BOOKING_BASE}/${encodeURIComponent(ref)}/verify-otp`,
        {otp},
    );

    if (!result.success) {
        return {
            success: false,
            token: null,
            user: null,
            message: result.message || result.error || 'Invalid code. Try again.',
            statusCode: result.statusCode,
        };
    }

    return {
        success: true,
        token: result.data?.token || null,
        user: result.data?.user || null,
        message: result.message || '',
    };
}

// A summary can name the passenger under any of these; the masked contract doesn't
// promise one, so we read whichever is there rather than picking a favourite.
function passengerIdOf(summary) {
    const id = summary?.user_id ?? summary?.passenger_id ?? summary?.passenger?.id ?? summary?.user?.id;
    return id === undefined || id === null ? null : String(id);
}

/**
 * Does the signed-in passenger own the trip this `ref` points at?
 *
 * No dedicated endpoint for this, and none is needed: the summary above already
 * identifies the trip, so the two existing calls answer it between them.
 *
 *  1. The summary names the passenger → compare ids and we're done in one request.
 *  2. It doesn't (the documented shape is masked) → ask for the trip itself with the
 *     session we're testing. This is only conclusive BECAUSE the summary came back 200:
 *     that proves the trip exists, so a refusal from `/trips/{id}` can only be about who
 *     is asking. On its own a 403/404 there would be ambiguous — that ambiguity was the
 *     whole argument for a separate `/ownership` endpoint, and the summary removes it.
 *
 * `owned: null` = couldn't tell (the summary itself failed, or neither route answered).
 * RefAuthGate carries on with the existing session on null: the trip endpoints refuse a
 * stranger's trip anyway, so the cost is an empty page, where guessing "not owned" would
 * push a legitimate passenger through an OTP they don't need.
 *
 * @param {string} ref
 * @param {object|null} currentUser  the signed-in user, from `GET /account`
 * @returns {{owned: boolean|null}}
 */
export async function checkRefOwnership(ref, currentUser) {
    const summary = await getPhoneBookingSummary(ref);
    // Includes 404 `quote_not_found` — a dead ref is the page's problem to report, not a
    // reason to send someone who is already signed in through an OTP.
    if (!summary.success || !summary.data) return {owned: null};

    const refUserId = passengerIdOf(summary.data);
    const meId = currentUser?.id === undefined || currentUser?.id === null ? null : String(currentUser.id);
    if (refUserId && meId) return {owned: refUserId === meId};

    const tripId = summary.data.trip_id;
    if (tripId === undefined || tripId === null) return {owned: null};

    // Cheapest shape of the call the confirm page is about to make anyway.
    const probe = await customersApiClient.get(`/trips/${encodeURIComponent(tripId)}?without_polyline=true`);
    if (probe.success) return {owned: true};
    if (probe.statusCode === 403 || probe.statusCode === 404) return {owned: false};
    // 401 (session died between here and `/account`), 5xx, offline — unknown, not "no".
    return {owned: null};
}
